import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import { useConfirm } from "../../../components/Dialog";
import { useAuth } from "../../../context/AuthContext";
import { LeadDetail } from "../../../types/leadDetail";
import Field, { FieldGrid } from "./Field";
import Section from "./Section";

type Props = {
  detail: LeadDetail;
  onReload: () => void;
};

type FormState = {
  name: string;
  lastName: string;
  lastName2: string;
  email: string;
  phone: string;
  gender: string;
  idNumber: string;
  citizenship: string;
  birthdate: string;
  nationality: string;
  nationality2: string;
  residenceInSpain: string;
  provider: string;
  job: string;
  jobType: string;
  riskJob: string;
  prp: string;
  prpDescription: string;
  address: string;
  number: string;
  door: string;
  zipcode: string;
  city: string;
  province: string;
  country: string;
};

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const isoDay = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

const toForm = (d: LeadDetail): FormState => ({
  name: d.name ?? "",
  lastName: d.lastName ?? "",
  lastName2: d.lastName2 ?? "",
  email: d.email ?? "",
  phone: d.phone ?? "",
  gender: d.gender ?? "",
  idNumber: d.idNumber ?? "",
  citizenship: d.citizenship ?? "",
  birthdate: isoDay(d.birthdate),
  nationality: d.nationality ?? "",
  nationality2: d.nationality2 ?? "",
  residenceInSpain: d.residenceInSpain ?? "",
  provider: d.provider ?? "",
  job: d.job ?? "",
  jobType: d.jobType ?? "",
  riskJob: d.riskJob ?? "",
  prp: d.prp ?? "",
  prpDescription: d.prpDescription ?? "",
  address: d.address?.address ?? "",
  number: d.address?.number ?? "",
  door: d.address?.door ?? "",
  zipcode: d.address?.zipcode ?? "",
  city: d.address?.city ?? "",
  province: d.address?.province ?? "",
  country: d.address?.country ?? "",
});

const trimToNull = (s: string): string | null => {
  const t = s.trim();
  return t === "" ? null : t;
};

export default function PersonalSection({ detail, onReload }: Props) {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(() => toForm(detail));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setForm(toForm(detail));
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const onChange = <K extends keyof FormState>(key: K, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await request<{ updated: boolean }>(
        `/api/admin/operations/leads/${detail.leadUlid}/personal`,
        {
          method: "PATCH",
          token,
          body: {
            name: trimToNull(form.name),
            lastName: trimToNull(form.lastName),
            lastName2: trimToNull(form.lastName2),
            email: trimToNull(form.email),
            phone: trimToNull(form.phone),
            gender: trimToNull(form.gender),
            idNumber: trimToNull(form.idNumber),
            citizenship: trimToNull(form.citizenship),
            birthdate: trimToNull(form.birthdate),
            nationality: trimToNull(form.nationality),
            nationality2: trimToNull(form.nationality2),
            residenceInSpain: trimToNull(form.residenceInSpain),
            provider: trimToNull(form.provider),
            job: trimToNull(form.job),
            jobType: trimToNull(form.jobType),
            riskJob: trimToNull(form.riskJob),
            prp: trimToNull(form.prp),
            prpDescription: trimToNull(form.prpDescription),
            address: trimToNull(form.address),
            number: trimToNull(form.number),
            door: trimToNull(form.door),
            zipcode: trimToNull(form.zipcode),
            city: trimToNull(form.city),
            province: trimToNull(form.province),
            country: trimToNull(form.country),
          },
        },
      );
      const hasContract =
        detail.contract !== null
        && (detail.contract.createdAt !== null
          || detail.contract.signatureLink !== null);
      if (hasContract) {
        const regen = await confirm({
          title: "Regenerar contrato",
          message:
            "Este lead ya tiene un contrato generado. ¿Quieres regenerarlo con"
            + " los nuevos datos personales? El cliente recibirá el nuevo enlace"
            + " de firma.",
          confirmLabel: "Sí, regenerar",
          cancelLabel: "No, sólo actualizar",
          tone: "warning",
        });
        if (regen) {
          try {
            await request<unknown>(
              `/api/admin/operations/leads/${detail.leadUlid}/contract/regenerate`,
              { method: "POST", token, body: {} },
            );
          } catch (err) {
            // Personal data already saved; surface the regen failure separately.
            setError(
              err instanceof HttpError
                ? `Datos guardados, pero falló la regeneración del contrato (HTTP ${err.status})`
                : "Datos guardados, pero falló la regeneración del contrato",
            );
            setSubmitting(false);
            onReload();
            return;
          }
        }
      }
      setEditing(false);
      onReload();
    } catch (err) {
      if (err instanceof HttpError) {
        const body = err.body as { error?: string; message?: string } | undefined;
        if (err.status === 409 && body?.error === "EMAIL_ALREADY_EXISTS") {
          setError("Ese email ya está en uso por otro lead");
        } else if (body?.message) {
          setError(`HTTP ${err.status}: ${body.message}`);
        } else {
          setError(`HTTP ${err.status}`);
        }
      } else {
        setError("No se pudo guardar");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const editButton = !editing ? (
    <button
      type="button"
      onClick={startEdit}
      style={editTriggerStyle}
      title="Editar datos personales"
    >
      Editar
    </button>
  ) : null;

  return (
    <Section
      title="Datos personales"
      defaultOpen
      accent="#0069d9"
      rightSlot={editButton}
    >
      {editing ? (
        <EditForm
          form={form}
          submitting={submitting}
          error={error}
          onChange={onChange}
          onSave={save}
          onCancel={cancel}
        />
      ) : (
        <ReadView detail={detail} />
      )}
    </Section>
  );
}

function ReadView({ detail }: { detail: LeadDetail }) {
  return (
    <>
      <FieldGrid>
        <Field label="Nombre" value={detail.name} />
        <Field label="Primer apellido" value={detail.lastName} />
        <Field label="Segundo apellido" value={detail.lastName2} />
        <Field label="Email" value={detail.email} copy />
        <Field label="Teléfono" value={detail.phone} copy />
        <Field label="DNI / NIE" value={detail.idNumber} copy mono />
        <Field label="Género" value={detail.gender} />
        <Field label="Nacimiento" value={fmtDate(detail.birthdate)} />
        <Field label="Nacionalidad" value={detail.nationality} />
        <Field label="Nacionalidad 2" value={detail.nationality2} />
        <Field label="Citizenship" value={detail.citizenship} />
        <Field label="Reside en España" value={detail.residenceInSpain} />
        <Field label="Provider" value={detail.provider} />
      </FieldGrid>

      <h3 style={subtitleStyle}>Profesión</h3>
      <FieldGrid>
        <Field label="Job" value={detail.job} />
        <Field label="Job type" value={detail.jobType} />
        <Field label="Risk job" value={detail.riskJob} />
        <Field label="PRP" value={detail.prp} />
        <Field label="PRP descripción" value={detail.prpDescription} />
      </FieldGrid>

      {detail.address && (
        <>
          <h3 style={subtitleStyle}>Dirección</h3>
          <FieldGrid>
            <Field label="Calle" value={detail.address.address} />
            <Field label="Número" value={detail.address.number} />
            <Field label="Puerta" value={detail.address.door} />
            <Field label="Código postal" value={detail.address.zipcode} />
            <Field label="Ciudad" value={detail.address.city} />
            <Field label="Provincia" value={detail.address.province} />
            <Field label="País" value={detail.address.country} />
          </FieldGrid>
        </>
      )}

      <h3 style={subtitleStyle}>Fechas (solo lectura)</h3>
      <FieldGrid>
        <Field label="Effect date" value={fmtDate(detail.effectDate)} />
        <Field
          label="Product questions"
          value={fmtDateTime(detail.productQuestionsRepliedAt)}
        />
        <Field
          label="Upsell completado"
          value={fmtDateTime(detail.upsell2CompletedAt)}
        />
      </FieldGrid>
    </>
  );
}

function EditForm({
  form,
  submitting,
  error,
  onChange,
  onSave,
  onCancel,
}: {
  form: FormState;
  submitting: boolean;
  error: string | null;
  onChange: <K extends keyof FormState>(key: K, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      <FieldGrid>
        <Input label="Nombre" value={form.name} onChange={(v) => onChange("name", v)} />
        <Input
          label="Primer apellido"
          value={form.lastName}
          onChange={(v) => onChange("lastName", v)}
        />
        <Input
          label="Segundo apellido"
          value={form.lastName2}
          onChange={(v) => onChange("lastName2", v)}
        />
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(v) => onChange("email", v)}
        />
        <Input label="Teléfono" value={form.phone} onChange={(v) => onChange("phone", v)} />
        <Input
          label="DNI / NIE"
          value={form.idNumber}
          onChange={(v) => onChange("idNumber", v)}
          mono
        />
        <Select
          label="Género"
          value={form.gender}
          onChange={(v) => onChange("gender", v)}
          options={[
            { value: "", label: "—" },
            { value: "male", label: "male" },
            { value: "female", label: "female" },
          ]}
        />
        <Input
          label="Nacimiento"
          type="date"
          value={form.birthdate}
          onChange={(v) => onChange("birthdate", v)}
        />
        <Input
          label="Nacionalidad"
          value={form.nationality}
          onChange={(v) => onChange("nationality", v)}
        />
        <Input
          label="Nacionalidad 2"
          value={form.nationality2}
          onChange={(v) => onChange("nationality2", v)}
        />
        <Input
          label="Citizenship"
          value={form.citizenship}
          onChange={(v) => onChange("citizenship", v)}
        />
        <Select
          label="Reside en España"
          value={form.residenceInSpain}
          onChange={(v) => onChange("residenceInSpain", v)}
          options={[
            { value: "", label: "—" },
            { value: "yes", label: "yes" },
            { value: "no", label: "no" },
          ]}
        />
        <Input
          label="Provider"
          value={form.provider}
          onChange={(v) => onChange("provider", v)}
        />
      </FieldGrid>

      <h3 style={subtitleStyle}>Profesión</h3>
      <FieldGrid>
        <Input label="Job" value={form.job} onChange={(v) => onChange("job", v)} />
        <Input
          label="Job type"
          value={form.jobType}
          onChange={(v) => onChange("jobType", v)}
        />
        <Input
          label="Risk job"
          value={form.riskJob}
          onChange={(v) => onChange("riskJob", v)}
        />
        <Input label="PRP" value={form.prp} onChange={(v) => onChange("prp", v)} />
        <Input
          label="PRP descripción"
          value={form.prpDescription}
          onChange={(v) => onChange("prpDescription", v)}
        />
      </FieldGrid>

      <h3 style={subtitleStyle}>Dirección</h3>
      <FieldGrid>
        <Input
          label="Calle"
          value={form.address}
          onChange={(v) => onChange("address", v)}
        />
        <Input
          label="Número"
          value={form.number}
          onChange={(v) => onChange("number", v)}
        />
        <Input label="Puerta" value={form.door} onChange={(v) => onChange("door", v)} />
        <Input
          label="Código postal"
          value={form.zipcode}
          onChange={(v) => onChange("zipcode", v)}
        />
        <Input label="Ciudad" value={form.city} onChange={(v) => onChange("city", v)} />
        <Input
          label="Provincia"
          value={form.province}
          onChange={(v) => onChange("province", v)}
        />
        <Input
          label="País"
          value={form.country}
          onChange={(v) => onChange("country", v)}
        />
      </FieldGrid>

      {error && (
        <div
          style={{
            marginTop: "0.75rem",
            padding: "0.5rem 0.75rem",
            borderRadius: "0.4rem",
            background: "#fde2e2",
            color: "#b32424",
            fontSize: "0.82rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          justifyContent: "flex-end",
          marginTop: "1rem",
          paddingTop: "0.75rem",
          borderTop: "1px solid #f1f3f5",
        }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          style={cancelBtn}
        >
          Cancelar
        </button>
        <button type="submit" disabled={submitting} style={saveBtn}>
          {submitting ? "Guardando…" : "Guardar"}
        </button>
      </div>
    </form>
  );
}

function Input({
  label,
  value,
  onChange,
  type = "text",
  mono = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  mono?: boolean;
}) {
  return (
    <label
      style={{
        display: "flex",
        gap: "0.75rem",
        padding: "0.4rem 0",
        borderTop: "1px dashed #f1f3f5",
        alignItems: "center",
      }}
    >
      <span style={inputLabelStyle}>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          padding: "0.4rem 0.55rem",
          borderRadius: "0.4rem",
          border: "1px solid #dee2e6",
          background: "#f8f9fb",
          fontSize: "0.88rem",
          fontFamily: mono ? "monospace" : "inherit",
        }}
      />
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <label
      style={{
        display: "flex",
        gap: "0.75rem",
        padding: "0.4rem 0",
        borderTop: "1px dashed #f1f3f5",
        alignItems: "center",
      }}
    >
      <span style={inputLabelStyle}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          padding: "0.4rem 0.55rem",
          borderRadius: "0.4rem",
          border: "1px solid #dee2e6",
          background: "#f8f9fb",
          fontSize: "0.88rem",
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

const inputLabelStyle: React.CSSProperties = {
  width: "30%",
  minWidth: 140,
  fontSize: ".75rem",
  color: "#73879C",
  textTransform: "uppercase",
  letterSpacing: ".3px",
  fontWeight: 600,
};

const editTriggerStyle: React.CSSProperties = {
  padding: "0.3rem 0.7rem",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#0069d9",
  border: "1px solid #0069d9",
  fontSize: ".75rem",
  fontWeight: 600,
  cursor: "pointer",
};

const cancelBtn: React.CSSProperties = {
  padding: "0.45rem 0.95rem",
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#73879C",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const saveBtn: React.CSSProperties = {
  padding: "0.45rem 1.1rem",
  borderRadius: "0.4rem",
  background: "#0069d9",
  color: "white",
  border: "1px solid #0069d9",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};

const subtitleStyle: React.CSSProperties = {
  margin: "1rem 0 0.3rem",
  fontSize: ".75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "#0069d9",
  letterSpacing: ".5px",
  borderBottom: "1px solid #f1f3f5",
  paddingBottom: ".2rem",
};
